import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["messages", "loader", "scrollTrigger", "scrollIndicator", "scrollIndicatorCount"]
  static values = {
    conversationId: Number,
    oldestLoadedId: Number,
    currentUserId: String,
    scrollThreshold: { type: Number, default: 100 },
    loadOlderUrl: String,
    markReadUrlTemplate: String,
    newMessageText: { type: String, default: "New message" },
    newMessagesText: { type: String, default: "%{count} new messages" },
  }

  oldestLoadedIdValueChanged() {
    this.isLoading = false
  }

  connect() {
    this.isLoading = false
    this.lastScrollTop = 0
    this.newMessagesCount = 0
    this.scrollToBottom()
    this.setupScrollListener()
    this.setupScrollTrigger()
    this.setupMessageObserver()
    this.markAllVisibleMessagesAsRead()
    
    this.element.addEventListener('chat:oldest-loaded-id-updated', () => {
      this.oldestLoadedIdValueChanged()
    })
  }

  markAllVisibleMessagesAsRead() {
    if (!this.hasMessagesTarget) return
    
    for (const msg of this.messagesTarget.children) {
      if (this.isMessageVisible(msg)) {
        this.markMessageAsReadIfNeeded(msg)
      }
    }
  }

  setupMessageObserver() {
    if (!this.hasMessagesTarget) return
    if (!this.currentUserIdValue) return

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.classList && node.classList.contains("message-item")) {
            this.markMessageAsReadIfNeeded(node)
            
            if (this.isLoading) return
            
            const senderId = node.dataset.messageSenderId
            const isSent = senderId === this.currentUserIdValue
            const isNearBottom = this.isNearBottom()
            const isFirstChild = this.messagesTarget.firstElementChild === node
            
            if ((isSent || isNearBottom) && !isFirstChild) {
              this.scrollToBottom()
              this.hideScrollIndicator()
            } else if (!isSent && !isNearBottom && !isFirstChild) {
              this.incrementNewMessagesCount()
            }
          }
        })
      })
    })

    observer.observe(this.messagesTarget, { childList: true })
  }

  isNearBottom() {
    const scrollTop = this.element.scrollTop
    const scrollHeight = this.element.scrollHeight
    const clientHeight = this.element.clientHeight
    return (scrollHeight - scrollTop - clientHeight) < this.scrollThresholdValue
  }

  markMessageAsReadIfNeeded(messageEl) {
    const senderId = messageEl.dataset.messageSenderId
    if (!senderId || senderId === this.currentUserIdValue) return

    if (!this.isMessageVisible(messageEl)) return

    const messageId = messageEl.id.replace("message_", "")
    if (!messageId) return

    const url = this.markReadUrlTemplateValue.replace('MESSAGE_ID_PLACEHOLDER', messageId)
    fetch(url, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        "Accept": "application/json"
      }
    }).catch((error) => {
      console.error("Failed to mark message as read:", error)
    })
  }

  isMessageVisible(messageEl) {
    const rect = messageEl.getBoundingClientRect()
    const containerRect = this.element.getBoundingClientRect()
    return rect.top >= containerRect.top && rect.bottom <= containerRect.bottom
  }

  setupScrollTrigger() {
    if (!this.hasScrollTriggerTarget) return

    const observer = new MutationObserver(() => {
      this.scrollToBottom()
    })
    observer.observe(this.scrollTriggerTarget, { attributes: true, attributeFilter: ['data-timestamp'] })
  }

  scrollToBottom() {
    setTimeout(() => {
      this.element.scrollTop = this.element.scrollHeight
    }, 100)
  }

  setupScrollListener() {
    let scrollTimeout
    let readCheckTimeout
    this.element.addEventListener("scroll", () => {
      const currentScrollTop = this.element.scrollTop
      const isScrollingUp = currentScrollTop < this.lastScrollTop
      this.lastScrollTop = currentScrollTop

      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        if (currentScrollTop <= 100 && isScrollingUp && this.oldestLoadedIdValue && !this.isLoading) {
          this.loadOlderMessages()
        }
      }, 150)

      clearTimeout(readCheckTimeout)
      readCheckTimeout = setTimeout(() => {
        if (this.isNearBottom()) {
          this.markAllVisibleMessagesAsRead()
          this.hideScrollIndicator()
        }
      }, 300)
    }, { passive: true })
  }

  loadOlderMessages() {
    if (this.isLoading || !this.oldestLoadedIdValue || !this.hasMessagesTarget) return

    this.isLoading = true
    const firstMessageBefore = this.messagesTarget.firstElementChild
    const scrollTopBefore = this.element.scrollTop
    const scrollHeightBefore = this.element.scrollHeight

    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove("hidden")
    }

    fetch(`${this.loadOlderUrlValue}?oldest_loaded_id=${this.oldestLoadedIdValue}`, {
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    .then(response => response.text())
    .then(html => {
      Turbo.renderStreamMessage(html)
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const scrollHeightAfter = this.element.scrollHeight
          const heightDifference = scrollHeightAfter - scrollHeightBefore
          const newScrollTop = scrollTopBefore + heightDifference
          
          if (firstMessageBefore && this.messagesTarget.contains(firstMessageBefore)) {
            this.element.scrollTop = newScrollTop
            this.lastScrollTop = newScrollTop
          }
          
          setTimeout(() => {
            this.isLoading = false
            if (this.hasLoaderTarget) {
              this.loaderTarget.classList.add("hidden")
            }
          }, 100)
        })
      })
    })
    .catch(() => {
      this.isLoading = false
      if (this.hasLoaderTarget) {
        this.loaderTarget.classList.add("hidden")
      }
    })
  }

  incrementNewMessagesCount() {
    this.newMessagesCount++
    this.showScrollIndicator()
  }

  showScrollIndicator() {
    if (!this.hasScrollIndicatorTarget) return
    this.scrollIndicatorTarget.classList.remove("hidden")
    this.updateIndicatorCount()
  }

  hideScrollIndicator() {
    if (!this.hasScrollIndicatorTarget) return
    this.scrollIndicatorTarget.classList.add("hidden")
    this.newMessagesCount = 0
  }

  updateIndicatorCount() {
    if (!this.hasScrollIndicatorCountTarget) return
    const text = this.newMessagesCount === 1 ? this.newMessageTextValue : this._interpolate(this.newMessagesTextValue, { count: this.newMessagesCount })
    this.scrollIndicatorCountTarget.textContent = text
  }

  scrollToBottomOnClick() {
    this.scrollToBottom()
    this.hideScrollIndicator()
    this.markAllVisibleMessagesAsRead()
  }

  _interpolate(text, params) {
    return text.replace(/%\{(\w+)\}/g, (_, key) => params[key] != null ? params[key] : "")
  }
}

